import { useState, useCallback, useEffect, useRef } from "react";
import { DataVisualizationContent } from "../../../../../types/newtypes";
import { useScoreTracking } from "../../../../../context/ScoreTrackingContext";

interface UseDataVisualizationPreviewProps {
  content: DataVisualizationContent;
  onCardStateChange?: (isCompleted: boolean, canProceed: boolean) => void;
  cardId: string;
}

// Robust correctness detector for legacy/new option shapes
const isOptionCorrect = (option: any): boolean => {
  if (typeof option?.__isCorrect === 'boolean') return option.__isCorrect;
  const v = option?.is_correct ?? option?.isCorrect ?? option?.correct;
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') return v.toLowerCase() === 'true' || v === '1';
  if (typeof v === 'number') return v === 1;
  return false;
};

export const useDataVisualizationPreview = ({
  content,
  onCardStateChange,
  cardId,
}: UseDataVisualizationPreviewProps) => {
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [isSelectionCorrect, setIsSelectionCorrect] = useState<boolean | null>(
    null,
  );
  const [feedbackTitle, setFeedbackTitle] = useState("");
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [isWhyModalOpen, setIsWhyModalOpen] = useState(false);

  const { incrementMistakes, updateCardScore, isCardCompleted, getMistakes } =
    useScoreTracking();
  const maxScore = content?.max_score ?? 10;
  const previousCardIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (
      previousCardIdRef.current !== null &&
      previousCardIdRef.current !== cardId
    ) {
      setSelectedAnswer(null);
      setIsSelectionCorrect(null);
      setFeedbackTitle("");
      setFeedbackMessage("");
      setIsWhyModalOpen(false);
    }
    previousCardIdRef.current = cardId;
  }, [cardId]);

  const evaluateSelection = useCallback(
    (index: number) => {
      const option = content?.options?.[index];
      if (typeof window !== 'undefined') {
        const optAny = option as any;
        console.debug('[DataViz] raw option', optAny, 'index', index, 'keys', option ? Object.keys(option) : null, 'is_correct', optAny?.is_correct, '__isCorrect', optAny?.__isCorrect);
      }
      if (!option) return;

      const isCorrect = isOptionCorrect(option);
      if (typeof window !== 'undefined') {
        // Temporary instrumentation to trace legacy correctness issues
        console.debug('[DataViz] isOptionCorrect result', isCorrect, 'for index', index);
      }
      setSelectedAnswer(index);
      setIsSelectionCorrect(isCorrect);

      const remediationText =
        (option?.remediation?.type === "text" &&
          option?.remediation?.content) ||
        "";

      if (isCorrect) {
        setFeedbackTitle("Correct!");
        setFeedbackMessage(remediationText);

        if (cardId && !isCardCompleted(cardId)) {
          const mistakes = getMistakes(cardId) || 0;
          updateCardScore(cardId, mistakes, maxScore);
        }

        onCardStateChange?.(true, true);
      } else {
        setFeedbackTitle("Not quite right");
        setFeedbackMessage(remediationText || "Keep going");

        if (cardId && !isCardCompleted(cardId)) {
          incrementMistakes(cardId, maxScore);
        }

        onCardStateChange?.(false, false);
      }
    },
    [
      content?.options,
      cardId,
      onCardStateChange,
      isCardCompleted,
      getMistakes,
      updateCardScore,
      incrementMistakes,
      maxScore,
    ],
  );

  const handleOptionSelect = useCallback(
    (index: number) => {
      if (isSelectionCorrect === true) return;
      evaluateSelection(index);
    },
    [evaluateSelection, isSelectionCorrect],
  );

  const handleResetAnswers = useCallback(() => {
    if (isSelectionCorrect === true) return;
    setSelectedAnswer(null);
    setIsSelectionCorrect(null);
    setFeedbackTitle("");
    setFeedbackMessage("");
    onCardStateChange?.(false, false);
  }, [isSelectionCorrect, onCardStateChange]);

  const getCorrectAnswerExplanation = useCallback(() => {
    const correctOption = content?.options?.find((option) => isOptionCorrect(option));
    return (
      (correctOption?.remediation?.type === "text" &&
        correctOption?.remediation?.content) ||
      "Explanation unavailable"
    );
  }, [content?.options]);

  return {
    selectedAnswer,
    isSelectionCorrect,
    feedbackTitle,
    feedbackMessage,
    handleOptionSelect,
    handleResetAnswers,
    isWhyModalOpen,
    setIsWhyModalOpen,
    getCorrectAnswerExplanation,
  };
};
